import React, { useState, useRef, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Clock,
  Users,
  UserCheck,
  UserX,
  Plus,
  Eye,
  Edit,
  Trash2,
  Calendar,
  ChevronDown,
  ChevronUp,
  Filter,
  Download,
  Upload,
  FileText,
  FileSpreadsheet,
  FileOutput,
  List,
  LayoutList,
  Grid,
  RefreshCcw,
  CheckCircle,
  AlertTriangle,
  TrendingUp,
  MousePointerClick,
  LogOut,
  CheckCircle2,
  X
} from 'lucide-react';
import { useTimeTracking } from '../../core/contexts/TimeTrackingContext';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import TimeTrackingPeriodToggle from './components/TimeTrackingPeriodToggle';
import { CustomSelect, CustomDatePicker } from '../../shared/components/custom';
import localApiAdapter from '../../core/services/localApiAdapter';
import QawaemErpCrudModal from '../../products/saudi/components/QawaemErpCrudModal';
import QawaemErpRowActions from '../../products/saudi/components/QawaemErpRowActions';
import QawaemClickableStatusBadge from '../../products/saudi/components/QawaemClickableStatusBadge';
import QawaemTableHeaderActionsCell from '../../products/saudi/components/QawaemTableHeaderActionsCell';
import TableColumnHeaderFilter from '../../shared/components/table/TableColumnHeaderFilter';
import { useColumnHeaderFilters } from '../../shared/hooks/useColumnHeaderFilters';
import { applyColumnFilters, textColumnRule } from '../../shared/utils/columnHeaderFilters';
import useModuleConfigTheme from '../../shared/hooks/useModuleConfigTheme';
import { getTimeTrackingCodeLabel, formatTimeTrackingCodeOption } from '../../shared/utils/timeTrackingI18n';
import {
  isLateCheckIn,
  isSaudiWeekend,
  isRamadanDate,
  getEffectiveDailyHours,
  getLateThresholdTime,
  formatPointageDate,
  getPointageHijriPref,
  setPointageHijriPref,
  getBrandLocale,
} from './pointageHelpers';
import FacturationListPageLayout, {
  facturationTableScrollClass,
} from '../facturation/components/FacturationListPageLayout';
import { useGlobalTheme } from '../../shared/hooks/useGlobalTheme';
import { isSaudiBrand } from '../../products/saudi/utils/isSaudiBrand';
import {
  usePointageModalBodyLock,
  getPointageModalFormClasses,
  buildPointageControlsStrip,
  buildPointageImportExportProps,
  buildPointageBulkDeleteFilterAction,
  POINTAGE_ACTIONS_TH_CLASS,
  mapPointageStatCards,
  buildPointageColumnVisibilityState,
  buildPointageQawaemColumnDefs,
  isPointageColumnVisible,
  createPointageHandleToggleColumn,
  pointageTableColSpan,
  PointageCrudNotificationToast,
  useAppNotificationToast,
  notifyBulkDeleted,
  notifyCreated,
  notifyDeleted,
  notifyError,
  notifyUpdated,
  sortRecordsNewestFirst,
  buildPointageTimeCodeStatusOptions,
  usePointageEntityReference,
  usePointageEmployeeOptions,
  resolvePointageEmployeeFromSelect,
  resolvePointageEmployeeSelectValue,
} from './pointageListPageUtils';
import PointageFormModalShell, { POINTAGE_FORM_GRID_CLASS } from './components/PointageFormModalShell';
import PointageEntityReferenceField from './components/PointageEntityReferenceField';
import RequiredLabelText from '../../shared/components/custom/RequiredLabelText';
import {
  QawaemTableSelectAllHeader,
  QawaemTableSelectRowCell,
  useQawaemFilteredRowSelection,
} from '../../products/saudi/components/QawaemTableSelection';
import { mergeStickySelectClass } from '../../products/saudi/components/QawaemTableStickyActions';

function pickEmployeeValue(emp) {
  return String(emp.matricule || emp.employeeId || emp.id || '');
}

function entryMatchesEmployee(entry, emp) {
  if (!entry || !emp) return false;
  const eid = String(entry.employeeId ?? entry.employee_id ?? '');
  const keys = [emp.matricule, emp.employeeId, emp.id]
    .filter((k) => k != null && k !== '')
    .map((k) => String(k));
  return keys.some((k) => k === eid);
}

const EMPTY_DAILY_STATS = { present: 0, absent: 0, late: 0, totalHours: 0 };

const DailyTimeTrackingPage = () => {
  const { t, i18n } = useTranslation();
  const { toast: notifyToast, show: showNotify, dismiss: dismissNotify } = useAppNotificationToast();
  const p = (key, opts) => t(`modules.pointage.pages.dailyTimeTrackingPage.${key}`, opts);
  const qawaem = isSaudiBrand();
  const { isDashboardLightMode } = useGlobalTheme();
  const {
    getTimeEntriesForDate,
    getDailyStats,
    addTimeEntry,
    deleteTimeEntry,
    timeTrackingCodes,
    employees,
    refreshTimeTrackingCodes,
  } = useTimeTracking();
  const [searchParams] = useSearchParams();
  
  // ===== STATE MANAGEMENT =====
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().slice(0, 10));
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState('table'); // 'table' | 'cards'
  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);
  
  // Date range filter
  const [dateRange, setDateRange] = useState({ start: '', end: '' });
  const [isDateRangeOpen, setIsDateRangeOpen] = useState(false);
  const [isStatusFilterOpen, setIsStatusFilterOpen] = useState(false);
  
  // Dropdown states
  const [showImportDropdown, setShowImportDropdown] = useState(false);
  const [showExportDropdown, setShowExportDropdown] = useState(false);
  
  // Modals
  const [showModal, setShowModal] = useState(false);
  const cfgTheme = useModuleConfigTheme();
  const modalForm = getPointageModalFormClasses(cfgTheme.light);
  usePointageModalBodyLock(showModal);
  const [modalMode, setModalMode] = useState('add');
  const [currentEntry, setCurrentEntry] = useState(null);
  
  // Quick filters
  const [quickFilter, setQuickFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showHijri, setShowHijri] = useState(() => getPointageHijriPref());

  // Data state
  const [timeTrackingEntries, setTimeTrackingEntries] = useState([]);
  const [dailyStats, setDailyStats] = useState(EMPTY_DAILY_STATS);
  const [loading, setLoading] = useState(false);

  const generateEntryRef = usePointageEntityReference('timeEntry', 'PNT', timeTrackingEntries, 'entryRef');
  const { employeeOptions } = usePointageEmployeeOptions(
    localApiAdapter.get.bind(localApiAdapter),
    { valueField: 'id' },
  );
  
  // Toast notifications
  const [toasts, setToasts] = useState([]);
  
  // Refs
  const importDropdownRef = useRef(null);
  const exportDropdownRef = useRef(null);
  const { filters: colFilters, setFilter: setColFilter, editingKey, setEditingKey } = useColumnHeaderFilters();
  const dateRangeRef = useRef(null);
  const statusFilterRef = useRef(null);

  // ===== EFFECTS =====
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (importDropdownRef.current && !importDropdownRef.current.contains(event.target)) {
        setShowImportDropdown(false);
      }
      if (exportDropdownRef.current && !exportDropdownRef.current.contains(event.target)) {
        setShowExportDropdown(false);
      }
      if (dateRangeRef.current && !dateRangeRef.current.contains(event.target)) {
        setIsDateRangeOpen(false);
      }
      if (statusFilterRef.current && !statusFilterRef.current.contains(event.target)) {
        setIsStatusFilterOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    const dateParam = searchParams.get('date');
    if (dateParam && /^\d{4}-\d{2}-\d{2}$/.test(dateParam)) {
      setSelectedDate(dateParam);
    }
  }, [searchParams]);

  useEffect(() => {
    refreshTimeTrackingCodes();
  }, [refreshTimeTrackingCodes]);

  // Fetch time tracking data when date changes
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [entries, stats] = await Promise.all([
          getTimeEntriesForDate(selectedDate),
          getDailyStats(selectedDate)
        ]);
        setTimeTrackingEntries(sortRecordsNewestFirst(Array.isArray(entries) ? entries : []));
        setDailyStats(stats || EMPTY_DAILY_STATS);
      } catch (error) {
        console.error('Error fetching time tracking data:', error);
        setTimeTrackingEntries([]);
        setDailyStats(EMPTY_DAILY_STATS);
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [selectedDate, getTimeEntriesForDate, getDailyStats]);

  const entriesForDate = useMemo(
    () => timeTrackingEntries.filter((e) => e.date === selectedDate),
    [timeTrackingEntries, selectedDate]
  );

  const activeEmployees = useMemo(
    () => employees.filter((e) => e.isActive),
    [employees]
  );

  const rosterRows = useMemo(
    () =>
      activeEmployees.map((employee) => ({
        employee,
        entry: entriesForDate.find((en) => entryMatchesEmployee(en, employee)) || null
      })),
    [activeEmployees, entriesForDate]
  );

  const filteredEntries = useMemo(
    () =>
      entriesForDate.filter((entry) => {
        const matchesSearch =
          !searchTerm ||
          (entry.employeeName &&
            entry.employeeName.toLowerCase().includes(searchTerm.toLowerCase())) ||
          String(entry.employeeId || '')
            .toLowerCase()
            .includes(searchTerm.toLowerCase()) ||
          entry.status.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesQuickFilter =
          quickFilter === 'all' ||
          (quickFilter === 'present' && (entry.status === 'P' || entry.status === 'P/2')) ||
          (quickFilter === 'absent' && entry.status && entry.status.includes('A')) ||
          (quickFilter === 'late' && entry.checkIn && isLateCheckIn(entry.checkIn));
        const matchesStatus = statusFilter === 'all' || entry.status === statusFilter;
        return matchesSearch && matchesQuickFilter && matchesStatus;
      }),
    [entriesForDate, searchTerm, quickFilter, statusFilter]
  );

  const filteredRosterRows = useMemo(() => {
    return rosterRows.filter(({ employee, entry }) => {
      const mat = String(employee.matricule || '');
      const matchesSearch =
        !searchTerm ||
        (employee.name && employee.name.toLowerCase().includes(searchTerm.toLowerCase())) ||
        mat.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (entry &&
          entry.employeeName &&
          entry.employeeName.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (entry && entry.status && entry.status.toLowerCase().includes(searchTerm.toLowerCase()));

      if (statusFilter !== 'all') {
        if (!entry || entry.status !== statusFilter) return false;
      }

      if (quickFilter === 'all') return matchesSearch;
      if (quickFilter === 'present') {
        // Only employees with present status
        return matchesSearch && entry && (entry.status === 'P' || entry.status === 'P/2');
      }
      if (quickFilter === 'absent') {
        // Employees with absence status OR no entry at all
        return matchesSearch && (!entry || (entry.status && entry.status.includes('A')));
      }
      if (quickFilter === 'late') {
        return matchesSearch && entry && entry.checkIn && isLateCheckIn(entry.checkIn);
      }
      return matchesSearch;
    });
  }, [rosterRows, searchTerm, statusFilter, quickFilter]);

  const rosterColumnFilterRules = useMemo(
    () => [
      textColumnRule('code', (row) => row.entry?.entryRef || row.entry?.entry_ref || ''),
      textColumnRule('matricule', (row) => pickEmployeeValue(row.employee) || ''),
      textColumnRule('employeeName', (row) => row.employee?.name || row.entry?.employeeName || ''),
      textColumnRule('arrival', (row) => row.entry?.checkIn || ''),
      textColumnRule('departure', (row) => row.entry?.checkOut || ''),
      textColumnRule('totalHours', (row) => row.entry?.totalHours || ''),
      textColumnRule('status', (row) => row.entry?.status || p('status_absentUnclocked')),
    ],
    [p],
  );

  const tableRosterRows = useMemo(
    () => applyColumnFilters(filteredRosterRows, colFilters, rosterColumnFilterRules),
    [filteredRosterRows, colFilters, rosterColumnFilterRules],
  );

  const selectableRosterRows = useMemo(
    () => tableRosterRows.filter((row) => row.entry?.id),
    [tableRosterRows],
  );

  const {
    selectedRecords,
    setSelectedRecords,
    allRowsSelected,
    someRowsSelected,
    handleSelectAll,
    handleSelectRecord,
  } = useQawaemFilteredRowSelection(selectableRosterRows, (row) => row.entry.id);

  useEffect(() => {
    if (selectedRecords.length > 0) setIsStatusFilterOpen(false);
  }, [selectedRecords]);

  const handleBulkDelete = async () => {
    if (selectedRecords.length === 0) return;
    if (window.confirm(p('confirm_delete'))) {
      try {
        const deleteCount = selectedRecords.length;
        await Promise.all(selectedRecords.map((id) => deleteTimeEntry(id)));
        setSelectedRecords([]);
        const [entries, stats] = await Promise.all([
          getTimeEntriesForDate(selectedDate),
          getDailyStats(selectedDate),
        ]);
        setTimeTrackingEntries(sortRecordsNewestFirst(Array.isArray(entries) ? entries : []));
        setDailyStats(stats || EMPTY_DAILY_STATS);
        notifyBulkDeleted(showNotify, deleteCount, p('title'));
      } catch (error) {
        console.error('Error bulk deleting time entries:', error);
        notifyError(showNotify, p('alert_deleteError'));
      }
    }
  };

  const openPointageForEmployee = (employee) => {
    const defaultStatus = timeTrackingCodes.find((code) => code.code === 'P') || timeTrackingCodes[0];
    const v = pickEmployeeValue(employee);
    setCurrentEntry({
      id: null,
      employeeId: v,
      employeeName: employee.name || '',
      date: selectedDate,
      checkIn: '',
      checkOut: '',
      totalHours: '0:00',
      status: defaultStatus?.code || 'P',
      statusColor: defaultStatus?.bgColor || 'bg-green-500',
      statusLabel: defaultStatus?.label || p('defaultPresent'),
      notes: ''
    });
    setModalMode('add');
    setShowModal(true);
  };

  // Toast notification system
  const showToast = (message, type = 'success') => {
    const id = Date.now();
    setToasts(prev => [...prev, { id, message, type }]);
    setTimeout(() => {
      setToasts(prev => prev.filter(t => t.id !== id));
    }, 4000);
  };

  // Helper: format date for display
  const getDateLabel = (dateStr) => {
    const today = new Date().toISOString().slice(0, 10);
    const yesterday = new Date(Date.now() - 86400000).toISOString().slice(0, 10);
    const tomorrow = new Date(Date.now() + 86400000).toISOString().slice(0, 10);
    
    if (dateStr === today) return { text: p('dateToday'), badge: 'bg-green-500/20 text-green-300 border-green-500/40' };
    if (dateStr === yesterday) return { text: p('dateYesterday'), badge: 'bg-orange-500/20 text-orange-300 border-orange-500/40' };
    if (dateStr === tomorrow) return { text: p('dateTomorrow'), badge: 'bg-blue-500/20 text-blue-300 border-blue-500/40' };
    
    const date = new Date(dateStr + 'T12:00:00');
    const locale = getBrandLocale(i18n.language);
    const gregorian = date.toLocaleDateString(locale, {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
    const text = showHijri
      ? formatPointageDate(dateStr, gregorian, { showHijri: true })
      : gregorian;
    const weekend = isSaudiWeekend(date);
    const ramadan = isRamadanDate(date);
    let badge = 'bg-white/10 text-white/80 border-white/30';
    if (weekend) badge = 'bg-indigo-500/20 text-indigo-200 border-indigo-400/40';
    else if (ramadan) badge = 'bg-emerald-500/20 text-emerald-200 border-emerald-400/40';
    return { text, badge, weekend, ramadan };
  };

  const toggleHijri = () => {
    const next = !showHijri;
    setShowHijri(next);
    setPointageHijriPref(next);
  };

  const entryIsLate = (entry) => entry?.checkIn && isLateCheckIn(entry.checkIn);

  // Quick point employee (direct click - no modal)
  const quickPointEmployee = async (employee) => {
    const now = new Date();
    const currentTime = now.toTimeString().slice(0, 5);
    const defaultStatus = timeTrackingCodes.find((code) => code.code === 'P') || timeTrackingCodes[0];
    const v = pickEmployeeValue(employee);
    
    const newEntry = {
      id: null,
      employeeId: v,
      employeeName: employee.name || '',
      date: selectedDate,
      checkIn: currentTime,
      checkOut: '',
      totalHours: '0:00',
      status: defaultStatus?.code || 'P',
      statusColor: defaultStatus?.bgColor || 'bg-green-500',
      statusLabel: defaultStatus?.label || p('defaultPresent'),
      notes: ''
    };

    try {
      await addTimeEntry(newEntry);
      showToast(p('toast_pointSuccess', { name: employee.name, time: currentTime }), 'success');
      
      // Refresh data
      const [entries, stats] = await Promise.all([
        getTimeEntriesForDate(selectedDate),
        getDailyStats(selectedDate)
      ]);
      setTimeTrackingEntries(sortRecordsNewestFirst(Array.isArray(entries) ? entries : []));
      setDailyStats(stats || EMPTY_DAILY_STATS);
    } catch (error) {
      console.error('Error quick point:', error);
      showToast(p('toast_pointError'), 'error');
    }
  };

  // Quick depoint (check out)
  const quickDeppointEmployee = async (entry) => {
    const now = new Date();
    const currentTime = now.toTimeString().slice(0, 5);
    
    // Calculate total hours
    const checkInTime = new Date(`2000-01-01T${entry.checkIn}`);
    const checkOutTime = new Date(`2000-01-01T${currentTime}`);
    const diffMs = checkOutTime - checkInTime;
    const hours = Math.floor(diffMs / 3600000);
    const minutes = Math.floor((diffMs % 3600000) / 60000);
    const totalHours = `${hours}:${minutes.toString().padStart(2, '0')}`;

    const updatedEntry = {
      ...entry,
      checkOut: currentTime,
      totalHours
    };

    try {
      await addTimeEntry(updatedEntry);
      showToast(p('toast_depointSuccess', { name: entry.employeeName, hours: totalHours }), 'success');
      
      // Refresh data
      const [entries, stats] = await Promise.all([
        getTimeEntriesForDate(selectedDate),
        getDailyStats(selectedDate)
      ]);
      setTimeTrackingEntries(sortRecordsNewestFirst(Array.isArray(entries) ? entries : []));
      setDailyStats(stats || EMPTY_DAILY_STATS);
    } catch (error) {
      console.error('Error quick depoint:', error);
      showToast(p('toast_depointError'), 'error');
    }
  };

  // Handle pointer button click (with Shift detection)
  const handlePointerClick = (employee, entry, event) => {
    if (event?.shiftKey) {
      // Shift+Click: open modal for custom entry
      openPointageForEmployee(employee);
    } else {
      // Normal click: quick point/depoint
      if (!entry) {
        // No entry: quick point
        quickPointEmployee(employee);
      } else if (entry && !entry.checkOut) {
        // Entry exists but no checkout: quick depoint
        quickDeppointEmployee(entry);
      }
    }
  };
  
  // Employee time-tracking codes only
  const availableStatuses = timeTrackingCodes.filter((code) =>
    code.type === 'employee' || (!code.type && !code.code.startsWith('V-') && !code.code.startsWith('E-'))
  );

  // Badge: colors from time-tracking codes (bgColor) or row statusColor
  const getStatusBadge = (entry) => {
    if (!entry) return null;
    const statusCode = timeTrackingCodes.find((code) => code.code === entry.status);
    const displayLabel = getTimeTrackingCodeLabel(
      entry.status,
      entry.statusLabel || statusCode?.label,
      t
    );
    const label = `${entry.status} - ${displayLabel}`.replace(/\s*-\s*$/, '').trim();

    const candidates = [
      entry.statusColor,
      statusCode?.bgColor,
      statusCode?.bg_color,
      statusCode?.color
    ].filter(Boolean);

    const tailwindBg = candidates.find((v) => String(v).trim().startsWith('bg-'));
    const hexBg = candidates.find((v) => /^#([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6}|[0-9A-Fa-f]{8})$/.test(String(v).trim()));

    if (hexBg) {
      return (
        <span
          className="px-3 py-1 rounded-full text-xs font-medium border border-white/25 shadow-sm"
          style={{ backgroundColor: hexBg, color: '#ffffff' }}
        >
          {label}
        </span>
      );
    }

    const bg = tailwindBg || 'bg-slate-600';
    return (
      <span
        className={`px-3 py-1 rounded-full text-xs font-medium border border-white/25 text-white shadow-sm ${bg}`}
      >
        {label}
      </span>
    );
  };

  const clickableStatusOptions = useMemo(
    () => availableStatuses.map((code) => ({
      value: code.code,
      label: getTimeTrackingCodeLabel(code.code, code.label, t),
      icon: CheckCircle,
      style: String(code.bgColor || code.bg_color || code.color || '').startsWith('bg-')
        ? `${code.bgColor || code.bg_color || code.color} text-white border-white/25`
        : 'bg-slate-500/20 text-white border-white/25',
    })),
    [availableStatuses, t],
  );

  const handleStatusChange = async (id, newStatus) => {
    const entry = timeTrackingEntries.find((e) => e.id === id);
    if (!entry) return;
    const statusCode = timeTrackingCodes.find((c) => c.code === newStatus);
    try {
      await addTimeEntry({
        ...entry,
        status: newStatus,
        statusColor: statusCode?.bgColor || statusCode?.bg_color || statusCode?.color,
        statusLabel: statusCode?.label,
      });
      const [entries, stats] = await Promise.all([
        getTimeEntriesForDate(selectedDate),
        getDailyStats(selectedDate),
      ]);
      setTimeTrackingEntries(sortRecordsNewestFirst(Array.isArray(entries) ? entries : []));
      setDailyStats(stats || EMPTY_DAILY_STATS);
    } catch (error) {
      console.error('Error updating status:', error);
    }
  };

  const handleAddTimeTracking = () => {
    const defaultCode = 'P';
    const defaultStatus = timeTrackingCodes.find(code => code.code === defaultCode) || timeTrackingCodes[0];
    
    setCurrentEntry({
      id: null,
      entryRef: generateEntryRef(),
      employeeId: '',
      employeeName: '',
      date: selectedDate,
      checkIn: '',
      checkOut: '',
      totalHours: '0:00',
      status: defaultStatus?.code || defaultCode,
      statusColor: defaultStatus?.bgColor || 'bg-green-500',
      statusLabel: defaultStatus?.label || p('defaultAvailable'),
      notes: ''
    });
    setModalMode('add');
    setShowModal(true);
  };

  const handleEditEntry = (entry) => {
    setCurrentEntry({...entry});
    setModalMode('edit');
    setShowModal(true);
  };
  
  const handleDeleteEntry = async (entryId) => {
    if (window.confirm(p('confirm_delete'))) {
      try {
        await deleteTimeEntry(entryId);
        // Refresh data after deletion
        const [entries, stats] = await Promise.all([
          getTimeEntriesForDate(selectedDate),
          getDailyStats(selectedDate)
        ]);
        setTimeTrackingEntries(sortRecordsNewestFirst(Array.isArray(entries) ? entries : []));
        setDailyStats(stats || EMPTY_DAILY_STATS);
        notifyDeleted(showNotify, p('title'));
      } catch (error) {
        console.error('Error deleting time entry:', error);
        notifyError(showNotify, p('alert_deleteError'));
      }
    }
  };
  
  const handleSaveEntry = async () => {
    if (!currentEntry?.employeeId && !currentEntry?.employeeName) {
      alert(p('alert_selectEmployee'));
      return;
    }

    if (currentEntry.checkIn && currentEntry.checkOut) {
      const checkInTime = new Date(`2000-01-01T${currentEntry.checkIn}`);
      const checkOutTime = new Date(`2000-01-01T${currentEntry.checkOut}`);

      if (checkOutTime <= checkInTime) {
        alert(p('alert_checkoutAfterCheckin'));
        return;
      }
    }

    let totalHours = currentEntry.totalHours || '0:00';
    if (currentEntry.checkIn && currentEntry.checkOut) {
      const checkInTime = new Date(`2000-01-01T${currentEntry.checkIn}`);
      const checkOutTime = new Date(`2000-01-01T${currentEntry.checkOut}`);
      const diffMs = checkOutTime - checkInTime;
      const hours = Math.floor(diffMs / 3600000);
      const minutes = Math.floor((diffMs % 3600000) / 60000);
      totalHours = `${hours}:${minutes.toString().padStart(2, '0')}`;
    }

    try {
      const statusCode = timeTrackingCodes.find((c) => c.code === currentEntry.status);
      const { employeeId, employeeName } = resolvePointageEmployeeFromSelect(
        currentEntry.employeeId,
        employeeOptions,
      );
      const entryToSave = {
        ...currentEntry,
        employeeId: employeeId ?? currentEntry.employeeId,
        employeeName: employeeName || currentEntry.employeeName,
        totalHours,
        entryRef: (currentEntry.entryRef || '').trim() || generateEntryRef(),
        statusColor: currentEntry.statusColor || statusCode?.bgColor || statusCode?.bg_color || statusCode?.color,
        statusLabel: currentEntry.statusLabel || statusCode?.label,
      };
      await addTimeEntry(entryToSave);
      if (modalMode === 'edit') {
        notifyUpdated(showNotify, p('title'));
      } else {
        notifyCreated(showNotify, p('title'));
      }
      setShowModal(false);
      setCurrentEntry(null);
      // Refresh data after adding/updating
      const [entries, stats] = await Promise.all([
        getTimeEntriesForDate(selectedDate),
        getDailyStats(selectedDate)
      ]);
      setTimeTrackingEntries(sortRecordsNewestFirst(Array.isArray(entries) ? entries : []));
      setDailyStats(stats || EMPTY_DAILY_STATS);
    } catch (error) {
      console.error('Error saving time entry:', error);
      notifyError(showNotify, p('alert_saveError')); return;
    }
  };

  const handleRefresh = async () => {
    setLoading(true);
    try {
      const [entries, stats] = await Promise.all([
        getTimeEntriesForDate(selectedDate),
        getDailyStats(selectedDate)
      ]);
      setTimeTrackingEntries(sortRecordsNewestFirst(Array.isArray(entries) ? entries : []));
      setDailyStats(stats || EMPTY_DAILY_STATS);
    } catch (error) {
      console.error('Error refreshing data:', error);
    } finally {
      setLoading(false);
    }
  };

  // ===== STATS =====
  const formatDailyTotalHours = (value) => {
    if (value == null || value === '') return '0:00';
    if (typeof value === 'string' && /^\s*\d{1,3}:\d{2}\s*$/.test(value)) return value.trim();
    const n = typeof value === 'number' ? value : parseFloat(String(value).replace(',', '.'));
    if (!Number.isFinite(n)) return '0:00';
    const totalMinutes = Math.round(n * 60);
    const h = Math.floor(totalMinutes / 60);
    const m = Math.abs(totalMinutes % 60);
    return `${h}:${m.toString().padStart(2, '0')}`;
  };

  const headcount = Math.max(activeEmployees.length, 1);
  const stats = {
    total: entriesForDate.length,
    effectif: activeEmployees.length,
    present: dailyStats.present ?? 0,
    absent: dailyStats.absent ?? 0,
    late: dailyStats.late ?? 0,
    totalHours: formatDailyTotalHours(dailyStats.totalHours),
    headcount
  };

  const listStats = mapPointageStatCards([
    { key: 'effectif', label: p('stats_activeHeadcount'), value: stats.effectif, icon: Users },
    { key: 'present', label: p('stats_present'), value: stats.present, icon: UserCheck },
    { key: 'absent', label: p('stats_absent'), value: stats.absent, icon: UserX },
    { key: 'late', label: p('stats_late'), value: stats.late, icon: AlertTriangle },
  ]);

  const importExportProps = buildPointageImportExportProps({
    showImportDropdown,
    setShowImportDropdown,
    showExportDropdown,
    setShowExportDropdown,
    importDropdownRef,
    exportDropdownRef,
    p,
    t,
  });

  const dateInfo = getDateLabel(selectedDate);

  const statusFilterOptions = useMemo(
    () => buildPointageTimeCodeStatusOptions(availableStatuses, (key, opts) => p(key, opts)),
    [availableStatuses, p],
  );

  const advancedOptionsContent = (
    <div className="space-y-4">
      <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-2 border border-white/20 shadow-xl shadow-black/10">
        <div className="flex space-x-1 overflow-x-auto hide-scrollbar">
          <button
            type="button"
            onClick={() => { setQuickFilter('all'); setStatusFilter('all'); setIsStatusFilterOpen(false); }}
            className={`flex-1 flex items-center justify-center space-x-2 px-4 py-3 rounded-2xl font-medium transition-all duration-300 ${quickFilter === 'all' && statusFilter === 'all' ? 'bg-gradient-to-r from-orange-600 to-red-600 text-white shadow-lg' : 'text-white/80 hover:bg-white/10 hover:text-white'}`}
          >
            <Grid className="h-5 w-5" />
            <span>{p('viewGeneral')}</span>
          </button>
          <button
            type="button"
            onClick={() => { setQuickFilter('present'); setStatusFilter('all'); setIsStatusFilterOpen(false); }}
            className={`flex-1 flex items-center justify-center space-x-2 px-4 py-3 rounded-2xl font-medium transition-all duration-300 ${quickFilter === 'present' ? 'bg-gradient-to-r from-orange-600 to-red-600 text-white shadow-lg' : 'text-white/80 hover:bg-white/10 hover:text-white'}`}
          >
            <UserCheck className="h-5 w-5" />
            <span>{p('viewByPresence')}</span>
          </button>
          <button
            type="button"
            onClick={() => { setQuickFilter('late'); setStatusFilter('all'); setIsStatusFilterOpen(false); }}
            className={`flex-1 flex items-center justify-center space-x-2 px-4 py-3 rounded-2xl font-medium transition-all duration-300 ${quickFilter === 'late' ? 'bg-gradient-to-r from-orange-600 to-red-600 text-white shadow-lg' : 'text-white/80 hover:bg-white/10 hover:text-white'}`}
          >
            <Clock className="h-5 w-5" />
            <span>{p('viewBySchedule')}</span>
          </button>
        </div>
      </div>

      <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-4 border border-white/20 shadow-xl shadow-black/10">
        <h3 className="text-white font-semibold mb-3 text-sm">{p('filter_by_status', { defaultValue: t('common.status') })}</h3>
        <div className="flex flex-wrap gap-2">
          {statusFilterOptions.map((opt) => (
            <button
              key={opt.value}
              type="button"
              onClick={() => { setStatusFilter(opt.value); setQuickFilter('all'); setIsStatusFilterOpen(false); }}
              className={`px-3 py-1.5 rounded-xl text-sm font-medium border transition-colors ${
                statusFilter === opt.value
                  ? 'bg-blue-500/30 text-blue-200 border-blue-400'
                  : 'bg-white/5 text-white/70 border-white/20 hover:bg-white/10'
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20 shadow-xl shadow-black/10">
        <h3 className="text-xl font-semibold text-white mb-4">{p('distributionTitle')}</h3>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="bg-white/5 rounded-xl p-4 border border-white/10">
            <div className="flex justify-between items-center mb-2">
              <h4 className="font-medium text-white">{p('distributionPresent')}</h4>
              <div className="text-sm rounded-full px-2 py-0.5 bg-green-500 text-white force-white">{stats.present}</div>
            </div>
            <div className="w-full bg-white/10 h-3 rounded-full">
              <div className="h-3 rounded-full bg-green-500" style={{ width: `${(stats.present / stats.headcount * 100).toFixed(1)}%` }} />
            </div>
            <p className="mt-2 text-sm text-white/60">{p('percentOfHeadcount', { percent: ((stats.present / stats.headcount) * 100).toFixed(1) })}</p>
          </div>
          <div className="bg-white/5 rounded-xl p-4 border border-white/10">
            <div className="flex justify-between items-center mb-2">
              <h4 className="font-medium text-white flex items-center gap-2">
                {p('distributionAbsent')}
                <span className="text-xs text-white/40 font-normal" title={p('absentIncludesUnclockedTooltip')}>ⓘ</span>
              </h4>
              <div className="text-sm rounded-full px-2 py-0.5 bg-red-500 text-white force-white">{stats.absent}</div>
            </div>
            <div className="w-full bg-white/10 h-3 rounded-full">
              <div className="h-3 rounded-full bg-red-500" style={{ width: `${((stats.absent / stats.headcount) * 100).toFixed(1)}%` }} />
            </div>
            <p className="mt-2 text-sm text-white/60">{p('percentOfHeadcount', { percent: ((stats.absent / stats.headcount) * 100).toFixed(1) })}</p>
          </div>
          <div className="bg-white/5 rounded-xl p-4 border border-white/10">
            <div className="flex justify-between items-center mb-2">
              <h4 className="font-medium text-white">{p('distributionLate')}</h4>
              <div className="text-sm rounded-full px-2 py-0.5 bg-orange-500 text-white force-white">
                {filteredEntries.filter(entryIsLate).length}
              </div>
            </div>
            <div className="w-full bg-white/10 h-3 rounded-full">
              <div className="h-3 rounded-full bg-orange-500" style={{ width: `${((filteredEntries.filter(entryIsLate).length / stats.headcount) * 100).toFixed(1)}%` }} />
            </div>
            <p className="mt-2 text-sm text-white/60">{p('percentOfHeadcount', { percent: ((filteredEntries.filter(entryIsLate).length / stats.headcount) * 100).toFixed(1) })}</p>
          </div>
        </div>
      </div>
    </div>
  );

  const POINTAGE_TABLE_COLUMNS = [
    { key: 'code', labelKey: 'table_code' },
    { key: 'matricule', labelKey: 'table_matricule' },
    { key: 'employeeName', labelKey: 'table_employeeName' },
    { key: 'arrival', labelKey: 'table_arrival' },
    { key: 'departure', labelKey: 'table_departure' },
    { key: 'totalHours', labelKey: 'table_totalHours' },
    { key: 'status', label: t('common.status') },
    { key: 'actions', label: t('common.actions'), required: true },
  ];
  const [columnVisibility, setColumnVisibility] = useState(() =>
    buildPointageColumnVisibilityState(POINTAGE_TABLE_COLUMNS),
  );
  const qawaemColumnDefs = useMemo(
    () => buildPointageQawaemColumnDefs(POINTAGE_TABLE_COLUMNS, p, columnVisibility),
    [p, t, columnVisibility],
  );
  const handleToggleColumn = createPointageHandleToggleColumn(setColumnVisibility, POINTAGE_TABLE_COLUMNS);
  const qawaemVisibleColumnCount = qawaemColumnDefs.filter((c) => c.checked !== false).length;
  const isColVisible = (key) => isPointageColumnVisible(columnVisibility, key);
  const tableColSpan = pointageTableColSpan(columnVisibility);

  // ===== RENDER =====
  return (
    <>
      <FacturationListPageLayout
        className="space-y-6"
        containerClassName="clients-container"
        title={p('title')}
        subtitle={p('subtitle')}
        icon={Clock}
        stats={listStats}
        viewMode={viewMode}
        onViewModeChange={setViewMode}
        filterActions={buildPointageBulkDeleteFilterAction({
          count: selectedRecords.length,
          onDelete: handleBulkDelete,
          label: `${t('common.delete')} (${selectedRecords.length})`,
          title: t('common.delete', { defaultValue: 'حذف' }),
        })}
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        searchPlaceholder={p('search_placeholder', { defaultValue: t('common.search', { defaultValue: 'بحث...' }) })}
        onCreate={handleAddTimeTracking}
        createLabel={p('addTimeTracking')}
        importExport={importExportProps}
        headerActions={
          <>
            <button
              type="button"
              onClick={toggleHijri}
              className={`border px-3 py-2 rounded-xl font-semibold transition-all duration-300 flex items-center gap-2 shrink-0 ${
                showHijri ? 'bg-indigo-500/20 border-indigo-400/40 text-indigo-200' : 'bg-white/5 border-white/20 text-white/80 hover:bg-white/10'
              }`}
              title={p('hijri_toggle_title')}
            >
              <Calendar className="h-4 w-4" />
              <span className="hidden sm:inline text-sm">{p('hijri_toggle')}</span>
            </button>
            <button type="button" onClick={handleRefresh} className="refresh-button" title={t('common.refresh')} disabled={loading}>
              <RefreshCcw size={20} className={loading ? 'animate-spin' : ''} />
            </button>
          </>
        }
        showAdvancedOptions={showAdvancedOptions}
        onToggleAdvancedOptions={() => setShowAdvancedOptions(!showAdvancedOptions)}
        advancedOptionsLabel={p('advancedOptions')}
        advancedOptionsContent={advancedOptionsContent}
        controlsStrip={buildPointageControlsStrip({
          qawaem,
          viewMode,
          onViewModeChange: setViewMode,
          isDashboardLightMode,
          searchTerm,
          onSearchChange: setSearchTerm,
          searchPlaceholder: p('search_placeholder', { defaultValue: t('common.search', { defaultValue: 'بحث...' }) }),
          cardsTitle: t('common.view_cards', { defaultValue: 'بطاقات' }),
          tableTitle: t('common.view_table', { defaultValue: 'جدول' }),
        })}
      >
        <div className="px-4 pt-2 pb-4 space-y-3">
          <TimeTrackingPeriodToggle />
          <div className="inline-flex items-center gap-2 flex-wrap">
            <Calendar size={16} className="text-white/50 shrink-0" />
            <span className="block w-full text-white/70 text-sm font-medium text-right">{p('trackingFor')}</span>
            <span className={`px-3 py-1.5 rounded-lg text-sm font-semibold border ${dateInfo.badge}`}>{dateInfo.text}</span>
            {dateInfo.weekend && (
              <span className="px-2 py-0.5 rounded-full text-xs bg-indigo-500/20 text-indigo-200 border border-indigo-400/30">{p('status_weekend')}</span>
            )}
            {dateInfo.ramadan && (
              <span className="px-2 py-0.5 rounded-full text-xs bg-emerald-500/20 text-emerald-200 border border-emerald-400/30">
                {p('status_ramadan', { hours: getEffectiveDailyHours(selectedDate) })}
              </span>
            )}
            <span className="text-white/50 text-xs">({selectedDate})</span>
            <span className="text-white/40 text-xs">{p('late_threshold', { time: getLateThresholdTime() })}</span>
          </div>
        </div>

        {viewMode === 'table' ? (
          <div className={facturationTableScrollClass(qawaem)}>
            <table className="w-full clients-table data-table">
              <thead className="glass-table-head">
                <tr>
                  <QawaemTableSelectAllHeader
                    allRowsSelected={allRowsSelected}
                    someRowsSelected={someRowsSelected}
                    onChange={handleSelectAll}
                    selectAllTitle={t('common.select_all', { defaultValue: 'تحديد الكل' })}
                    deselectAllTitle={t('common.deselect_all', { defaultValue: 'إلغاء تحديد الكل' })}
                  />
                  {isColVisible('code') && (
                    <TableColumnHeaderFilter columnKey="code" label={p('table_code')} value={colFilters.code ?? ''} onChange={(v) => setColFilter('code', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white" />
                  )}
                  {isColVisible('matricule') && (
                    <TableColumnHeaderFilter columnKey="matricule" label={p('table_matricule')} value={colFilters.matricule ?? ''} onChange={(v) => setColFilter('matricule', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white" />
                  )}
                  {isColVisible('employeeName') && (
                    <TableColumnHeaderFilter columnKey="employeeName" label={p('table_employeeName')} value={colFilters.employeeName ?? ''} onChange={(v) => setColFilter('employeeName', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white" />
                  )}
                  {isColVisible('arrival') && (
                    <TableColumnHeaderFilter columnKey="arrival" label={p('table_arrival')} value={colFilters.arrival ?? ''} onChange={(v) => setColFilter('arrival', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white" />
                  )}
                  {isColVisible('departure') && (
                    <TableColumnHeaderFilter columnKey="departure" label={p('table_departure')} value={colFilters.departure ?? ''} onChange={(v) => setColFilter('departure', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white" />
                  )}
                  {isColVisible('totalHours') && (
                    <TableColumnHeaderFilter columnKey="totalHours" label={p('table_totalHours')} value={colFilters.totalHours ?? ''} onChange={(v) => setColFilter('totalHours', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white" />
                  )}
                  {isColVisible('status') && (
                    <TableColumnHeaderFilter columnKey="status" label={t('common.status')} value={colFilters.status ?? ''} onChange={(v) => setColFilter('status', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} align="center" thClassName="text-white" />
                  )}
                  <QawaemTableHeaderActionsCell
                    label={t('common.actions')}
                    columns={qawaemColumnDefs}
                    onToggle={handleToggleColumn}
                    visibleCount={qawaemVisibleColumnCount}
                    totalCount={qawaemColumnDefs.length}
                    thClassName={POINTAGE_ACTIONS_TH_CLASS}
                  />
                </tr>
              </thead>
              <tbody className="bg-white/5">
                {activeEmployees.length === 0 ? (
                  <tr>
                    <td colSpan={tableColSpan} className="px-6 py-12 text-center text-white/50">
                      {p('empty_noActiveEmployees')}
                    </td>
                  </tr>
                ) : tableRosterRows.length > 0 ? (
                  tableRosterRows.map(({ employee, entry }) => {
                    const matLabel = pickEmployeeValue(employee) || '—';
                    const rowKey = entry?.id ?? `roster-${matLabel}`;
                    return (
                      <tr key={rowKey} className="hover:bg-white/10 transition-colors border-b border-white/10">
                        {entry?.id ? (
                          <QawaemTableSelectRowCell
                            checked={selectedRecords.includes(entry.id)}
                            onChange={() => handleSelectRecord(entry.id)}
                          />
                        ) : (
                          <td className={mergeStickySelectClass('px-3 py-4 text-center align-middle')} />
                        )}
                        {isColVisible('code') && (
                          <td className="px-6 py-4">
                            <span className="text-white font-semibold font-mono">{entry?.entryRef || entry?.entry_ref || '—'}</span>
                          </td>
                        )}
                        {isColVisible('matricule') && (
                          <td className="px-6 py-4">
                            <span className="px-2 py-1 bg-blue-500/20 text-blue-300 rounded font-mono text-sm">{matLabel}</span>
                          </td>
                        )}
                        {isColVisible('employeeName') && (
                          <td className="px-6 py-4 text-white font-semibold">{employee.name || entry?.employeeName || '—'}</td>
                        )}
                        {isColVisible('arrival') && (
                          <td className="px-6 py-4 text-white/80">
                            {entry?.checkIn || <span className="text-white/50">--:--</span>}
                          </td>
                        )}
                        {isColVisible('departure') && (
                          <td className="px-6 py-4 text-white/80">
                            {entry?.checkOut || <span className="text-white/50">--:--</span>}
                          </td>
                        )}
                        {isColVisible('totalHours') && (
                          <td className="px-6 py-4 text-white font-medium">{entry?.totalHours ?? '—'}</td>
                        )}
                        {isColVisible('status') && (
                          <td className="px-6 py-4 text-center">
                            {entry ? (
                              <div className="flex justify-center">
                                <QawaemClickableStatusBadge
                                  status={entry.status}
                                  recordId={entry.id}
                                  options={clickableStatusOptions}
                                  onChange={handleStatusChange}
                                  title={p('click_change_status', { defaultValue: t('common.change_status') })}
                                />
                              </div>
                            ) : (
                              <span className="px-3 py-1 rounded-full text-xs font-medium bg-red-500/20 text-red-300 border border-red-500/30 flex items-center gap-1.5 w-fit mx-auto">
                                <UserX size={14} />
                                {p('status_absentUnclocked')}
                              </span>
                            )}
                          </td>
                        )}
                        <td className="px-6 py-4 text-center align-middle">
                          <div className="flex items-center justify-center gap-1">
                            {entry && entry.checkOut ? (
                              <QawaemErpRowActions
                                onEdit={() => handleEditEntry(entry)}
                                onDelete={() => handleDeleteEntry(entry.id)}
                                editTitle={t('common.edit')}
                                deleteTitle={t('common.delete')}
                                compact
                              />
                            ) : entry && !entry.checkOut ? (
                              <>
                                <button
                                  type="button"
                                  onClick={(e) => handlePointerClick(employee, entry, e)}
                                  className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-orange-500/25 text-orange-200 border border-orange-500/40 text-xs font-medium hover:bg-orange-500/35 transition-colors"
                                  title={p('depointTitle')}
                                >
                                  <LogOut size={14} />
                                  {p('action_depoint')}
                                </button>
                                <QawaemErpRowActions
                                  onEdit={() => handleEditEntry(entry)}
                                  onDelete={() => handleDeleteEntry(entry.id)}
                                  editTitle={t('common.edit')}
                                  deleteTitle={t('common.delete')}
                                  compact
                                />
                              </>
                            ) : (
                              <button
                                type="button"
                                onClick={(e) => handlePointerClick(employee, entry, e)}
                                className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-emerald-500/25 text-emerald-200 border border-emerald-500/40 text-xs font-medium hover:bg-emerald-500/35 transition-colors"
                                title={p('pointTitle')}
                              >
                                <MousePointerClick size={14} />
                                {p('action_point')}
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan={tableColSpan} className="px-6 py-12 text-center text-white/50">
                                            {p('empty_noFilterMatch')}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        ) : (
          // Card View
          <div className="p-6">
            {activeEmployees.length === 0 ? (
              <p className="text-center text-white/50 py-12">
                {p('empty_noActiveEmployees')}
              </p>
            ) : filteredRosterRows.length === 0 ? (
              <p className="text-center text-white/50 py-12">
                                      {p('empty_noFilterMatch')}
              </p>
            ) : (
              <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
                {filteredRosterRows.map(({ employee, entry }) => {
                  const matLabel = pickEmployeeValue(employee) || '—';
                  const cardKey = entry?.id ?? `roster-${matLabel}`;
                  return (
                    <motion.div
                      key={cardKey}
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      className="bg-white/10 backdrop-blur-lg rounded-2xl p-6 border border-white/20 shadow-xl shadow-black/10 hover:bg-white/15 transition-all duration-300 h-full min-w-0"
                    >
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center space-x-2 mb-2 flex-wrap">
                            <span className="px-2 py-1 bg-blue-500/20 text-blue-300 rounded font-mono text-sm">{matLabel}</span>
                            {entry ? getStatusBadge(entry) : (
                              <span className="px-3 py-1 rounded-full text-xs font-medium bg-red-500/20 text-red-300 border border-red-500/30 flex items-center gap-1.5">
                                <UserX size={14} />
                                {p('status_absentUnclocked')}
                              </span>
                            )}
                          </div>
                          <h3 className="text-white font-semibold text-lg mb-1 truncate">
                            {employee.name || entry?.employeeName || '—'}
                          </h3>
                        </div>

                        <div className="flex items-center gap-1 ml-4 shrink-0">
                          {entry && entry.checkOut ? (
                            <QawaemErpRowActions
                              onEdit={() => handleEditEntry(entry)}
                              onDelete={() => handleDeleteEntry(entry.id)}
                              editTitle={t('common.edit')}
                              deleteTitle={t('common.delete')}
                              compact
                            />
                          ) : entry && !entry.checkOut ? (
                            <>
                              <button
                                type="button"
                                onClick={(e) => handlePointerClick(employee, entry, e)}
                                className="inline-flex items-center gap-1 px-2 py-1.5 rounded-lg bg-orange-500/25 text-orange-200 border border-orange-500/40 text-xs font-medium hover:bg-orange-500/35"
                                title={p('depointTitleShort')}
                              >
                                <LogOut size={14} />
                                {p('action_depoint')}
                              </button>
                              <QawaemErpRowActions
                                onEdit={() => handleEditEntry(entry)}
                                onDelete={() => handleDeleteEntry(entry.id)}
                                editTitle={t('common.edit')}
                                deleteTitle={t('common.delete')}
                                compact
                              />
                            </>
                          ) : (
                            <button
                              type="button"
                              onClick={(e) => handlePointerClick(employee, entry, e)}
                              className="inline-flex items-center gap-1 px-2 py-1.5 rounded-lg bg-emerald-500/25 text-emerald-200 border border-emerald-500/40 text-xs font-medium hover:bg-emerald-500/35"
                              title={p('pointTitleShort')}
                            >
                              <MousePointerClick size={14} />
                              {p('action_point')}
                            </button>
                          )}
                        </div>
                      </div>

                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <span className="text-white/50 text-xs">{p('table_arrival')}</span>
                          <span className="text-white font-semibold">{entry?.checkIn || '--:--'}</span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-white/50 text-xs">{p('table_departure')}</span>
                          <span className="text-white font-semibold">{entry?.checkOut || '--:--'}</span>
                        </div>
                        <div className="flex items-center justify-between border-t border-white/10 pt-2">
                          <span className="text-white/50 text-xs">{p('card_hoursWorked')}</span>
                          <span className="text-white font-semibold text-lg">{entry?.totalHours ?? '—'}</span>
                        </div>
                      </div>

                      {entry?.notes && (
                        <div className="bg-white/5 rounded-lg p-3 mt-4">
                          <p className="text-white/50 text-xs mb-1">{p('card_notes')}</p>
                          <p className="text-white text-sm">{entry.notes}</p>
                        </div>
                      )}
                    </motion.div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </FacturationListPageLayout>

      {/* Toast Notifications */}
      <AnimatePresence>
        {toasts.map((toast) => (
          <motion.div
            key={toast.id}
            initial={{ opacity: 0, y: -50, x: '-50%' }}
            animate={{ opacity: 1, y: 0, x: '-50%' }}
            exit={{ opacity: 0, y: -50, x: '-50%' }}
            className="fixed top-6 left-1/2 z-[9999] min-w-[320px] max-w-md"
          >
            <div className={`
              backdrop-blur-xl rounded-2xl p-4 shadow-2xl border
              ${toast.type === 'success' 
                ? 'bg-green-500/90 border-green-400/50 text-white' 
                : 'bg-red-500/90 border-red-400/50 text-white'}
            `}>
              <div className="flex items-center gap-3">
                {toast.type === 'success' ? (
                  <CheckCircle2 className="h-6 w-6 shrink-0" />
                ) : (
                  <X className="h-6 w-6 shrink-0" />
                )}
                <p className="text-sm font-medium flex-1">{toast.message}</p>
                <button
                  onClick={() => setToasts(prev => prev.filter(t => t.id !== toast.id))}
                  className="p-1 hover:bg-white/20 rounded-lg transition-colors shrink-0"
                >
                  <X size={16} />
                </button>
              </div>
            </div>
          </motion.div>
        ))}
      </AnimatePresence>
      
      {/* Add / edit time entry modal */}
      {showModal && currentEntry ? (
        <QawaemErpCrudModal
          open={showModal}
          onClose={() => { setShowModal(false); setCurrentEntry(null); }}
          title={modalMode === 'add' ? p('modal_addTimeTracking') : p('modal_editTimeTracking')}
          icon={UserCheck}
          onSubmit
          formId="pointage-form"
          saveLabel={modalMode === 'add' ? t('common.save') : p('update')}
          maxWidth="1000px"
          testId="daily-time-tracking-crud-modal"
        >
          <PointageFormModalShell
            isLight={cfgTheme.light}
            tabs={[]}
            activeTab=""
            onTabChange={() => {}}
            formId="pointage-form"
            onSubmit={(e) => { e.preventDefault(); handleSaveEntry(); }}
            ariaLabel={modalMode === 'add' ? p('modal_addTimeTracking') : p('modal_editTimeTracking')}
          >
            <div className="space-y-4">
              <div className={POINTAGE_FORM_GRID_CLASS}>
                <PointageEntityReferenceField
                  label={p('field_code')}
                  value={currentEntry.entryRef || ''}
                  onChange={(v) => setCurrentEntry({ ...currentEntry, entryRef: v })}
                  onRegenerate={modalMode === 'edit' ? undefined : () => setCurrentEntry({ ...currentEntry, entryRef: generateEntryRef() })}
                  placeholder={p('ph_code')}
                  autoGeneratedHint={modalMode === 'edit' ? undefined : p('auto_ref_hint', { defaultValue: 'يُولَّد تلقائياً' })}
                  regenerateTitle={t('common.regenerate', { defaultValue: 'إعادة توليد الرمز' })}
                  inputClassName="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white placeholder-white/30 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all"
                  labelClassName={modalForm.label}
                />
                <div>
                  <label className={modalForm.label}>{<RequiredLabelText text={p('label_employee')} />}</label>
                  <CustomSelect
                    value={resolvePointageEmployeeSelectValue(currentEntry, employeeOptions)}
                    onChange={(v) => {
                      const { employeeId, employeeName } = resolvePointageEmployeeFromSelect(v, employeeOptions);
                      setCurrentEntry({
                        ...currentEntry,
                        employeeId,
                        employeeName,
                      });
                    }}
                    options={employeeOptions}
                    placeholder={p('placeholder_selectEmployee')}
                    searchable={true}
                    autoSelect={false}
                  />
                </div>
              </div>
              <div className={POINTAGE_FORM_GRID_CLASS}>
                <div>
                  <label className={modalForm.label}>{p('label_date')}</label>
                  <CustomDatePicker
                    value={currentEntry.date || selectedDate}
                    onChange={(value) => setCurrentEntry({ ...currentEntry, date: value })}
                    placeholder={p('placeholder_selectDate')}
                  />
                </div>
                <div>
                  <label className={modalForm.label}>{p('label_status')}</label>
                  <CustomSelect
                    value={currentEntry.status || 'P'}
                    onChange={(value) => {
                      const selectedCode = timeTrackingCodes.find(code => code.code === value);
                      setCurrentEntry({
                        ...currentEntry,
                        status: value,
                        statusLabel: selectedCode?.label || '',
                        statusColor: selectedCode?.bgColor || selectedCode?.color || 'bg-green-500'
                      });
                    }}
                    options={availableStatuses.map(status => ({
                      value: status.code,
                      label: formatTimeTrackingCodeOption(status.code, status.label, t)
                    }))}
                    placeholder={p('placeholder_selectStatus')}
                    searchable={true}
                  />
                </div>
              </div>
              <div className={POINTAGE_FORM_GRID_CLASS}>
                <div>
                  <label className={modalForm.label}>{p('label_arrivalTime')}</label>
                  <input
                    type="time"
                    value={currentEntry.checkIn || ''}
                    onChange={(e) => setCurrentEntry({ ...currentEntry, checkIn: e.target.value })}
                    className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all [color-scheme:dark]"
                  />
                </div>
                <div>
                  <label className={modalForm.label}>{p('label_departureTime')}</label>
                  <input
                    type="time"
                    value={currentEntry.checkOut || ''}
                    onChange={(e) => setCurrentEntry({ ...currentEntry, checkOut: e.target.value })}
                    className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all [color-scheme:dark]"
                  />
                </div>
              </div>
              <div className={POINTAGE_FORM_GRID_CLASS}>
                <div className="md:col-span-3">
                  <label className={modalForm.label}>{p('label_notes')}</label>
                  <textarea
                    value={currentEntry.notes || ''}
                    onChange={(e) => setCurrentEntry({ ...currentEntry, notes: e.target.value })}
                    rows={3}
                    className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all resize-none"
                    placeholder={p('placeholder_notes')}
                  />
                </div>
              </div>
            </div>
          </PointageFormModalShell>
        </QawaemErpCrudModal>
      ) : null}
      <PointageCrudNotificationToast toast={notifyToast} onDismiss={dismissNotify} />
    </>
  );
};

export default DailyTimeTrackingPage;

